/**
 * Oendies verzendverzekering: toggle in the cart drawer and on the cart page.
 *
 * Keeps exactly one insurance line in the cart while the toggle is on and the
 * cart has at least one other product; removes it as soon as the toggle goes
 * off or nothing else is left. The customer's choice is stored as a cart
 * attribute, so it survives reloads and drawer re-renders.
 * Config comes from snippets/oendies-shipping-protection.liquid.
 */
import { CartUpdateEvent } from '@theme/events';

const SOURCE = 'oendies-shipping-protection';
const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const TOGGLE = '[data-oendies-sp-toggle]';
const config = window.OendiesShippingProtection;
// Own requests use the fetch from before our patch so they don't retrigger the sync.
const baseFetch = window.fetch.bind(window);

/** @type {'on' | 'off' | null} Choice made in the UI, not yet saved to the cart. */
let pendingChoice = null;
let running = false;
let rerun = false;

function root() {
  return window.Shopify?.routes?.root || '/';
}

// Section ids of every cart-items-component, split by drawer and cart page.
function cartSections() {
  const drawer = [];
  const page = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (!(el instanceof HTMLElement) || !el.dataset.sectionId) return;
    (el.closest('cart-drawer-component') ? drawer : page).push(el.dataset.sectionId);
  });
  return { drawer, page };
}

function visibleItemCount(cart) {
  const insured = cart.items
    .filter((item) => item.variant_id === config.variantId)
    .reduce((sum, item) => sum + item.quantity, 0);
  return cart.item_count - insured;
}

async function updateCart(updates, attributes) {
  const { drawer, page } = cartSections();
  // The header group renders empty on some product URLs, so render the drawer
  // from the locale root unless the cart page itself needs updating.
  const sectionsUrl = page.length ? window.location.pathname : root().replace(/\/$/, '') || '/';
  const body = { updates, sections: [...drawer, ...page].join(','), sections_url: sectionsUrl };
  if (attributes) body.attributes = attributes;

  const res = await baseFetch(`${root()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);
  const cart = await res.json();
  const sections = cart.sections || {};
  const hadFocus = document.activeElement instanceof HTMLElement && document.activeElement.matches(TOGGLE);

  // The Oendies cart drawer (assets/cart-drawer.js) applies this HTML in place.
  document.dispatchEvent(new CustomEvent('oendies:cart-refresh', { detail: { sections } }));
  if (page.length) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: visibleItemCount(cart), sections })
    );
  }
  if (hadFocus) {
    const toggle = document.querySelector(`dialog[open] ${TOGGLE}`) || document.querySelector(TOGGLE);
    if (toggle instanceof HTMLElement) toggle.focus({ preventScroll: true });
  }
}

async function sync() {
  const cart = await baseFetch(`${root()}cart.js`, { cache: 'no-store' }).then((r) => r.json());
  const lines = cart.items.filter((item) => item.variant_id === config.variantId);
  const hasProducts = cart.items.length > lines.length;

  const choice = pendingChoice;
  pendingChoice = null;
  const stored = cart.attributes?.[config.attribute];
  const state = choice || stored || (config.defaultOn ? 'on' : 'off');
  const wanted = state === 'on' && hasProducts ? 1 : 0;

  const updates = {};
  if (wanted && !lines.length) updates[config.variantId] = 1;
  lines.forEach((line, index) => {
    const quantity = index === 0 ? wanted : 0;
    if (line.quantity !== quantity) updates[line.key] = quantity;
  });
  const attributes = choice && choice !== stored ? { [config.attribute]: choice } : null;

  if (Object.keys(updates).length || attributes) await updateCart(updates, attributes);
}

async function run() {
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    do {
      rerun = false;
      await sync();
    } while (rerun);
  } catch (error) {
    console.error('[oendies-shipping-protection]', error);
    // Show the real cart state again instead of a toggle that lies.
    document.dispatchEvent(new CustomEvent('oendies:cart-refresh'));
  } finally {
    running = false;
  }
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  return input instanceof Request ? input.url : String(input);
}

if (config) {
  document.addEventListener('change', (event) => {
    const toggle = event.target;
    if (!(toggle instanceof HTMLInputElement) || !toggle.matches(TOGGLE)) return;
    pendingChoice = toggle.checked ? 'on' : 'off';
    // Keep a second toggle (drawer + cart page) in step right away.
    document.querySelectorAll(TOGGLE).forEach((el) => {
      if (el instanceof HTMLInputElement) el.checked = toggle.checked;
    });
    run();
  });

  // The custom cart drawer changes the cart without theme events, so watch
  // cart requests directly.
  window.fetch = function (input, init) {
    const request = baseFetch(input, init);
    if (CART_MUTATION.test(requestUrl(input).split('#')[0])) {
      request.then((res) => res.ok && run()).catch(() => {});
    }
    return request;
  };
  run();
}
